import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { renewSession } from '@/features/auth/api';
import { configureAuth } from '@/lib/api/client';
import type { LoginSuccess, UserInfo, UserRole } from '@/types/user';
import { hasRole, isDisplay } from '@/types/user';

/**
 * The session: a token, the user it belongs to, and when it dies.
 *
 * A person's token lives five hours and then stops working; there is no refresh for
 * it, and signing out means dropping it here. So for a person this module has three
 * jobs — hold the session, persist it across reloads, and end it the moment it
 * stops being usable.
 *
 * A wall screen (`DISPLAY`) has a fourth: its token lives thirty days, and this
 * module renews it once a day through `POST /login/refresh`, so the screen never
 * sees the login page while it is on. An administrator who ends the screen's
 * sessions ends this too — the next request answers 401 and the client signs out.
 */

const STORAGE_KEY = 'session';

/** How old a wall screen's token gets before it is renewed. */
const RENEW_AFTER_MS = 24 * 60 * 60 * 1000;

/** After a failed renewal: the token still has weeks to run, so the retry is calm. */
const RENEW_RETRY_MS = 15 * 60 * 1000;

/** The longest delay `setTimeout` honours; a longer one fires at once. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

interface StoredSession {
  token: string;
  elkToken: string | null;
  user: UserInfo;
  /** Epoch ms, read from the token's own `exp`. */
  expiresAt: number;
}

/**
 * Reads a time claim — `exp` or `iat` — out of a JWT, and nothing else.
 *
 * Knowing when the token dies lets the app end the session cleanly instead of
 * waiting for the next request to 401 in the middle of something; knowing when it
 * was issued tells a wall screen when to renew it. The role is NOT read from here:
 * this token has no role claim, and the server takes the role from the database on
 * every request anyway.
 */
const timeClaimOf = (token: string, claim: 'exp' | 'iat'): number | null => {
  const payload = token.split('.')[1];
  if (!payload) return null;

  try {
    // base64url → base64, then a UTF-8-safe decode: the payload can carry a
    // non-ASCII username, and `atob` alone would mangle it.
    const json = new TextDecoder().decode(
      Uint8Array.from(atob(payload.replace(/-/g, '+').replace(/_/g, '/')), (char) =>
        char.charCodeAt(0),
      ),
    );

    const seconds = (JSON.parse(json) as Partial<Record<'exp' | 'iat', unknown>>)[claim];
    return typeof seconds === 'number' ? seconds * 1000 : null;
  } catch {
    // A token we cannot read is still worth sending — only the server's opinion of
    // it matters. It just gets no proactive expiry.
    return null;
  }
};

const readStored = (): StoredSession | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const stored = JSON.parse(raw) as StoredSession;
    if (!stored.token || !stored.user) return null;

    // An expired token is worse than no token: it makes the app look signed in and
    // then fails the first request it makes.
    if (stored.expiresAt && stored.expiresAt <= Date.now()) return null;

    return stored;
  } catch {
    return null;
  }
};

/**
 * The token the API client sends, held outside React.
 *
 * The client needs a getter that is stable for the life of the app, and a request
 * in flight must never read a token from a closure that has since been replaced.
 * Writing a ref during render would do this too — and is unsafe, because a render
 * can be thrown away. This is written only from the two functions that also write
 * the state, so the two cannot disagree.
 */
let activeToken: string | null = null;

const persist = (session: StoredSession | null): void => {
  activeToken = session?.token ?? null;

  try {
    if (session) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Private mode or blocked storage: the session still works for this tab.
  }
};

// Wired once, at module load: `configureAuth` needs no React state, only these two
// stable functions, and doing it here keeps it out of an effect that would re-run.
let handleSessionEnded: () => void = () => {};

configureAuth({
  getToken: () => activeToken,
  onSessionEnded: () => handleSessionEnded(),
});

interface AuthContextValue {
  user: UserInfo | null;
  token: string | null;
  elkToken: string | null;
  isAuthenticated: boolean;
  signIn: (session: LoginSuccess) => void;
  signOut: () => void;
  /**
   * Replaces the stored user without touching the token.
   *
   * The session's copy of the user is taken at login and never refreshed, so an edit
   * would leave the header greeting somebody by their old surname until they signed
   * in again.
   *
   * **Only ever the caller's OWN account.** That means a `/user/me` response, or an
   * admin edit whose id is the caller's own — an administrator editing somebody else
   * must not call this, because it would put another person's account behind this
   * session's token and every role check would then read from the wrong user.
   */
  updateUser: (user: UserInfo) => void;
  /** Whether the signed-in user holds one of these roles. */
  can: (roles: readonly UserRole[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  // Read during initialisation, not in an effect: localStorage is synchronous, so
  // there is nothing to wait for — and an effect would render one frame signed out,
  // which is exactly long enough for a route guard to redirect a signed-in reader
  // to the login screen.
  const [session, setSession] = useState<StoredSession | null>(() => {
    const stored = readStored();
    activeToken = stored?.token ?? null;
    return stored;
  });

  const signOut = useCallback(() => {
    setSession(null);
    persist(null);
  }, []);

  const signIn = useCallback((incoming: LoginSuccess) => {
    const next: StoredSession = {
      token: incoming.token,
      elkToken: incoming.elkToken,
      user: incoming.userInfo,
      // The token's own claim is the truth — five hours for a person, thirty days for
      // a wall screen. The fallback, the shorter of the two, only matters if it could
      // not be parsed.
      expiresAt: timeClaimOf(incoming.token, 'exp') ?? Date.now() + 5 * 60 * 60 * 1000,
    };

    setSession(next);
    persist(next);
  }, []);

  /**
   * The stored user, replaced. The token and its expiry are untouched — nothing
   * about the session itself changed, only what is known about the person holding
   * it.
   */
  const updateUser = useCallback(
    (user: UserInfo) => {
      if (!session) return;

      const next: StoredSession = { ...session, user };
      setSession(next);
      persist(next);
    },
    [session],
  );

  // The API client's 401 handler, kept pointing at the current signOut.
  useEffect(() => {
    handleSessionEnded = signOut;
    return () => {
      handleSessionEnded = () => {};
    };
  }, [signOut]);

  /**
   * Ends the session at the token's expiry rather than at the next failed request,
   * so a reader who left a tab open comes back to a login screen instead of to a
   * page whose every action fails.
   *
   * Always through a timer, even when the token is already dead: calling signOut
   * straight from the effect body would set state during the commit and cascade a
   * second render.
   */
  useEffect(() => {
    if (!session?.expiresAt) return undefined;

    // A wall screen's thirty days are past what a timer can wait — a longer delay
    // fires at once, and would sign the screen out the moment it signed in. It renews
    // daily, long before the end; and a token that does run out is still caught, by
    // the 401 of its next request or by `readStored` on the next load.
    const remaining = session.expiresAt - Date.now();
    if (remaining > MAX_TIMEOUT_MS) return undefined;

    const timer = window.setTimeout(signOut, Math.max(0, remaining));
    return () => window.clearTimeout(timer);
  }, [session?.expiresAt, signOut]);

  /**
   * A wall screen renews its token once a day, so it is never sent to the login page
   * while it is on — there is nobody standing at it to sign in again.
   *
   * Timed from the token's own `iat`, so a reload does not restart the day, and a
   * screen switched on with a token older than that renews at once. A failed renewal
   * is retried calmly: the token still has weeks to run. An ENDED session is not a
   * failure to retry — it answers 401, and the API client signs out.
   */
  useEffect(() => {
    if (!session || !isDisplay(session.user)) return undefined;

    const issuedAt = timeClaimOf(session.token, 'iat') ?? Date.now();
    let cancelled = false;
    let timer: number | undefined;

    const renew = () => {
      renewSession()
        .then((renewed) => {
          if (!cancelled) signIn(renewed);
        })
        .catch(() => {
          if (!cancelled) timer = window.setTimeout(renew, RENEW_RETRY_MS);
        });
    };

    timer = window.setTimeout(renew, Math.max(0, issuedAt + RENEW_AFTER_MS - Date.now()));
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [session, signIn]);

  /**
   * Signing out in one tab signs out the others.
   *
   * Two tabs on one shared terminal is the normal case here, and one of them still
   * showing an operations table after the other signed out is both confusing and
   * wrong. `storage` only fires in the OTHER tabs, so this cannot loop.
   */
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;

      const stored = readStored();
      activeToken = stored?.token ?? null;
      setSession(stored);
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session?.user ?? null,
      token: session?.token ?? null,
      elkToken: session?.elkToken ?? null,
      isAuthenticated: session !== null,
      signIn,
      signOut,
      updateUser,
      can: (roles) => hasRole(session?.user ?? null, roles),
    }),
    [session, signIn, signOut, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }

  return context;
};
